import React, { useCallback, useState } from 'react';
import Button from '@atlaskit/button/new';
import EmptyState from '@atlaskit/empty-state';
import { useIntl } from '@atlassian/jira-intl';
import PadlockKey from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/error/components/padlock-key/index.tsx';
import { getAkEmptyStateRenderImageFn } from '@atlassian/jira-illustrations/src/ui/helpers/ak-empty-state/index.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useIssueKey, useBoardId } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useProjectKey } from '@atlassian/jira-project-context-service/src/main.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { requestAccess } from '../../services/request-access-service.tsx';
import { messages } from './messages.tsx';
import { ANALYTICS_SOURCE_NAME } from './constants.tsx';

export const NewPermissionErrorView = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { showFlag } = useFlagsService();
	const { push } = useRouterActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const accountId = useAccountId();
	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const boardId = useBoardId();

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isRequested, setIsRequested] = useState(false);

	const REQUEST_URL = '/rest/internal/latest/project-access/access-requested';

	const handleRequestAccessClick = useCallback(async () => {
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'requestProjectAccess');

		if (!accountId || !issueKey) {
			return;
		}
		setIsSubmitting(true);
		await requestAccess(
			{
				accountId,
				projectKey,
				issueKey,
				boardId,
			},
			REQUEST_URL,
		);
		// Always show success flag, regardless of server response details
		showFlag({
			title: messages.requestAccessSuccessTitle,
			type: 'success',
			isAutoDismiss: true,
			messageType: 'transactional',
			messageId:
				'issue-view-errors.ui.new-permission-error-view.show-flag.success.flag.success.request-access-success',
			actions: [
				{
					content: messages.requestAccessSuccessAction,
					onClick: () => {
						push('/jira/your-work');
					},
				},
			],
		});
		setIsRequested(true);
		setIsSubmitting(false);
	}, [accountId, issueKey, projectKey, boardId, showFlag, push, createAnalyticsEvent]);

	return (
		<ContextualAnalyticsData
			sourceName={ANALYTICS_SOURCE_NAME}
			sourceType={SCREEN}
			attributes={{ requestAccessButtonShown: !!accountId }}
		>
			<EmptyState
				testId="issue-view-errors.ui.new-permission-error-view.permission-error-view"
				header={formatMessage(messages.permissionErrorHeader)}
				description={formatMessage(messages.permissionErrorDescription)}
				renderImage={getAkEmptyStateRenderImageFn(PadlockKey)}
				primaryAction={
					accountId && !isRequested ? (
						<Button
							appearance="primary"
							onClick={handleRequestAccessClick}
							isLoading={isSubmitting}
							interactionName="jira-issue-view-errors.ui.new-permission-error-view.request-access-button"
						>
							{formatMessage(messages.requestAccessButton)}
						</Button>
					) : null
				}
			/>
			<FireScreenAnalytics />
		</ContextualAnalyticsData>
	);
};

export default NewPermissionErrorView;
